import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, FileText } from "lucide-react";
import api from "@/lib/api";

/** Interview Center "My Resume" track, no resume context yet: lets a learner who has NOT built
 * a resume inside Cloudivo drop in an existing resume file (PDF/DOCX/TXT) and immediately get
 * personalized interview questions from it -- without creating/overwriting a Resume Builder draft. */
export default function UploadResumeForInterviewCard({ onExtracted }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: extracted } = await api.post("/resume-checker/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post("/resume-builder/interview-context-from-text", { resume_text: extracted.resume_text });
      onExtracted(data);
      toast.success(`Got it — personalizing questions for "${data.target_role}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read this file — try a different one");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="glass-panel border border-rose-400/20 rounded-2xl p-8 mt-6 text-center" data-testid="upload-resume-for-interview-card">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center mx-auto mb-4">
        {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <FileText className="w-6 h-6 text-white" />}
      </div>
      <p className="font-heading text-lg font-semibold text-white mb-1.5">Upload your resume for a personalized interview</p>
      <p className="text-sm text-slate-400 mb-5 max-w-md mx-auto">
        {uploading ? "Reading your resume and preparing personalized questions…" : "Any role, any industry — not just Salesforce. Drop in a PDF, DOCX, or TXT resume and we'll generate interview questions based on it."}
      </p>
      <button
        onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-resume-for-interview-btn"
        className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
      >
        <UploadCloud className="w-4 h-4" /> {uploading ? "Please wait…" : "Choose Resume File"}
      </button>
      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="upload-resume-for-interview-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}
